import { X, Globe, Server, Network, Shield, ExternalLink } from "lucide-react";
import { cn } from "@asm/ui";
import type { GraphNode } from "../../lib/api";
import { formatNodeProperties, getNodeColor, STATUS_COLORS, VISIBILITY_COLORS } from "./types";

interface NodeDetailsPanelProps {
  node: GraphNode | null;
  onClose: () => void;
  onNavigate?: (nodeId: string) => void;
}

const TypeIcons: Record<string, typeof Globe> = {
  domain: Globe,
  subdomain: Globe,
  ip: Server,
  port: Network,
};

export function NodeDetailsPanel({ node, onClose, onNavigate }: NodeDetailsPanelProps) {
  if (!node) return null;

  const Icon = TypeIcons[node.node_type] || Shield;
  const properties = formatNodeProperties(node);
  const nodeColor = getNodeColor(node);

  return (
    <div className="absolute right-4 top-4 w-80 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-10">
      {/* Header */}
      <div
        className="px-4 py-3 border-b border-border flex items-center justify-between"
        style={{ backgroundColor: `${nodeColor}15` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="p-1.5 rounded-md"
            style={{ backgroundColor: nodeColor }}
          >
            <Icon className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{node.label}</p>
            <p className="text-xs text-muted-foreground capitalize">{node.node_type}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-muted rounded-md transition-colors"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Status badges */}
      <div className="px-4 py-2 flex gap-2 border-b border-border">
        <span
          className="px-2 py-0.5 text-xs font-medium rounded-full"
          style={{
            backgroundColor: `${STATUS_COLORS[node.status]}20`,
            color: STATUS_COLORS[node.status]
          }}
        >
          {node.status}
        </span>
        {node.visibility && (
          <span
            className="px-2 py-0.5 text-xs font-medium rounded-full"
            style={{
              backgroundColor: `${VISIBILITY_COLORS[node.visibility]}20`,
              color: VISIBILITY_COLORS[node.visibility]
            }}
          >
            {node.visibility}
          </span>
        )}
        {node.risk_score !== undefined && node.risk_score > 0 && (
          <span
            className={cn(
              "px-2 py-0.5 text-xs font-medium rounded-full",
              node.risk_score >= 7
                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                : node.risk_score >= 4
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            )}
          >
            Risk: {node.risk_score.toFixed(1)}
          </span>
        )}
      </div>

      {/* Properties */}
      <div className="px-4 py-3 space-y-2 max-h-64 overflow-y-auto">
        {properties.map((prop, idx) => (
          <div key={idx} className="flex justify-between gap-4">
            <span className="text-xs text-muted-foreground shrink-0">{prop.key}</span>
            <span className="text-xs font-medium text-right truncate">{prop.value}</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      {onNavigate && node.node_type !== "domain" && (
        <div className="px-4 py-3 border-t border-border">
          <button
            onClick={() => onNavigate(node.id)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-md transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            View Asset Details
          </button>
        </div>
      )}
    </div>
  );
}
